import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import './UserAnalytics.css';

export default function UserAnalytics() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userActivities, setUserActivities] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await fetch('/.netlify/functions/analytics?action=dashboard', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setStats(data);
      } else {
        setError(data.error || 'Failed to fetch analytics');
      }
    } catch (err) {
      setError('Failed to fetch analytics');
      console.error('Failed to fetch analytics', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserActivities = async (userId) => {
    try {
      const response = await fetch(`/.netlify/functions/analytics?action=user-activities&userId=${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserActivities(data);
      }
    } catch (err) {
      console.error('Failed to fetch user activities');
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    fetchUserActivities(user.id);
  };

  if (loading) return <div>Loading analytics...</div>;
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!stats) return <div>No analytics data available</div>;

  return (
    <div className="user-analytics">
      <h2>User Analytics</h2>

      <div className="analytics-summary">
        <div className="stat-card">
          <h3>Total Visits</h3>
          <p className="stat-number">{stats.totals?.total_visits || 0}</p>
        </div>
        <div className="stat-card">
          <h3>Total Activities</h3>
          <p className="stat-number">{stats.totals?.total_activities || 0}</p>
        </div>
      </div>

      <div className="analytics-content">
        <div className="user-stats-list">
          <h3>User Activity</h3>
          <div className="stats-table">
            <table>
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Logins</th>
                  <th>Activities</th>
                  <th>Last Login</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {stats.user_stats?.map((stat) => (
                  <tr key={stat.id}>
                    <td>{stat.username}</td>
                    <td>{stat.login_count || 0}</td>
                    <td>{stat.total_activities || 0}</td>
                    <td>
                      {stat.last_login
                        ? new Date(stat.last_login).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => handleSelectUser(stat)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {selectedUser && (
          <div className="user-details">
            <h3>Activities for {selectedUser.username}</h3>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => setSelectedUser(null)}
            >
              Close
            </button>
            <div className="activities-list">
              {userActivities.length > 0 ? (
                <ul>
                  {userActivities.map((activity, idx) => (
                    <li key={idx} className="activity-item">
                      <strong>{activity.action_type}</strong>
                      {activity.course_name && ` - ${activity.course_name}`}
                      {activity.page_url && ` (${activity.page_url})`}
                      <br />
                      <small>{new Date(activity.timestamp).toLocaleString()}</small>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No activities recorded</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
